import { AttachmentType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import type { BufferedAttachment } from '@queries/attachments.query';
import { EditIcon, FileIcon, LinkIcon, XIcon } from 'lucide-react';
import type { FunctionComponent } from 'react';

type BufferedAttachmentsListProps = {
  attachments: BufferedAttachment[];
  onEdit?: (attachment: BufferedAttachment) => void;
  onRemove?: (clientId: string) => void;
};

export const BufferedAttachmentsList: FunctionComponent<BufferedAttachmentsListProps> = (props) => {
  const { attachments, onEdit, onRemove } = props;

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {attachments.map((attachment) => {
        const Icon = attachment.type === AttachmentType.URL ? LinkIcon : FileIcon;

        return (
          <div
            key={attachment.clientId}
            className="group flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
          >
            <div className="shrink-0 size-10 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
              <Icon
                className="text-gray-400"
                size={18}
              />
            </div>

            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-700 truncate">{attachment.title}</div>
              {attachment.type === AttachmentType.URL && attachment.url && (
                <div className="text-xs text-gray-400 truncate">{attachment.url}</div>
              )}
              {attachment.type === AttachmentType.FILE && attachment.file && (
                <div className="text-xs text-gray-400 truncate">{attachment.file.name}</div>
              )}
            </div>

            {onEdit && (
              <IconButton
                ariaLabel="Edit attachment"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                icon={<EditIcon size={14} />}
                onClick={() => onEdit(attachment)}
                size="sm"
                variant="secondary"
              />
            )}

            {onRemove && (
              <IconButton
                ariaLabel="Remove attachment"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                icon={<XIcon size={14} />}
                onClick={() => onRemove(attachment.clientId)}
                size="sm"
                variant="secondary"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
